import { Upload, type UploadOptions } from "tus-js-client";

import type { FileMetaType, FileWithMetaType } from "@/hooks/UseTusHooks/Generics";

/** What a composer's upload needs from the app: where uploads go, which stage they are for, and who is sending. */
export type ComposerUploadConfig = {
  tusUrl: string;
  stage: string;
  accountId: string;
  preferredLanguage: string;
};

/** The tus client an upload goes through; the real one by default, a fake in tests. */
export type ComposerUploadClient = new (
  file: File,
  options: UploadOptions,
) => {
  start(): void;
};

/** The metadata's values as the strings tus carries, leaving out what `parseFile` left unset. */
function stringValues(meta: FileMetaType): Record<string, string> {
  return Object.fromEntries(
    Object.entries(meta)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)]),
  );
}

/**
 * The tus metadata a composer's file uploads with: the parsed file's own, plus the channel and the feed item the
 * upload pipeline posts it to. The pipeline reads the keys lower-cased off the S3 object (`feedid`, `feeditemid`).
 * `filename` is the file's full name, as `parseFile` read it, so the artifact downloads under it; `useTusUpload`
 * sends the display name cut to fit a chip instead.
 */
export function composerUploadMetadata(
  meta: FileMetaType,
  feedId: string,
  config: ComposerUploadConfig,
): Record<string, string> {
  return {
    ...stringValues(meta),
    feedItemId: meta.itemId,
    feedId,
    accountId: config.accountId,
    myAccount: config.accountId,
    stage: config.stage,
    preferredLanguage: config.preferredLanguage,
    startTime: new Date().toISOString(),
  };
}

/**
 * Uploads a parsed file to `feedId` and resolves once the upload service holds it, with the ids its feed item carries.
 *
 * @param parsed - The file and the metadata `parseFile` read from it.
 * @param Client - The tus client; a test passes one that settles on demand.
 */
export function startComposerUpload(
  parsed: FileWithMetaType,
  feedId: string,
  config: ComposerUploadConfig,
  Client: ComposerUploadClient = Upload,
): Promise<{ contentId: string; itemId: string }> {
  const { contentId, itemId } = parsed.meta;
  return new Promise((resolve, reject) => {
    const upload = new Client(parsed.file, {
      endpoint: config.tusUrl,
      metadata: composerUploadMetadata(parsed.meta, feedId, config),
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      onSuccess: () => resolve({ contentId, itemId }),
      onError: (error) => reject(error),
    });
    upload.start();
  });
}
